import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActiveCartFacade, ConsignmentEntry, PromotionLocation, PromotionResult } from '@spartacus/cart/base/root';
import { FeatureConfigService, GlobalMessageService, GlobalMessageType } from '@spartacus/core';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CartExtendService, UdpateMemoPayload } from 'src/app/service/cart/cart-extend.service';
import { ComponentService } from 'src/app/service/component.service';
import { CartItemComponentOptions, Item } from '../cart-item/cart-item.component';

@Component({
  selector: 'cx-cart-item-list',
  templateUrl: './cart-item-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CartItemListComponent {
  @Input() readonly = false;
  @Input() pageId: string;
  @Input() noStockError: any;
  @Input() lowStockError: any;
  @Input() options: CartItemComponentOptions = {
    isSaveForLater: false,
    optionalBtn: null,
  };
  @Input() dateFormatted: string;
  @Input() skipVariantCategory: string;
  @Input() cartPageId: string;
  @Input() appliedPromotions: any = [];
  @Input() unapprovedError = false;

  @Input() showWarehouseFeature: boolean;

  private _items: Item[] = [];
  form: FormGroup;

  cartItemProductCodesList: string[] = [];
  cartPageTemplate = document.querySelector('cx-page-layout.CartPageTemplate');
  dealerSpinner = false;
  removeSpinner = false;

  @Input('items')
  // TODO: currently we're getting a new array of items if the cart changes.
  // pretty annoying as it forces a repaint on the screen,
  // which is noticable in the UI.
  set items(items: Item[]) {
    let updatedItems = false;
    while (this.cartItemProductCodesList.length > 0) {
      this.cartItemProductCodesList.pop();
      updatedItems = true;
    }
    items.forEach((item) => {
      this.cartItemProductCodesList.push(item.product.code);
    });
    this.resolveItems(items);
    this.createForm();

    if (updatedItems) {
      this.dealerSpinner = false;
      this.removeSpinner = false;
      this.componentService.setMiniCartLoading(false);

      document.documentElement.style.overflow = 'initial';
      this.cdr.detectChanges();
    }
  }
  get items(): Item[] {
    return this._items;
  }

  get inStockItems(): Item[] {
    if (this.pageId === this.cartPageId) {
      return this._items.filter((item) => item.product.stock.stockLevelStatus === 'inStock' && item.updateable);
    } else {
      return this._items.filter((item) => item.backordered === false);
    }
  }
  get backOrderItems(): Item[] {
    if (this.pageId === this.cartPageId) {
      return this._items.filter((item) => item.product.stock.stockLevelStatus === 'backorder' && item.updateable);
    } else {
      return this._items.filter(
        (item) =>
          item.backordered === true &&
          (item.product.stock.stockLevelStatus === 'backorder' || item.product.stock.stockLevelStatus === 'inStock')
      );
    }
  }
  get outStockItems(): Item[] {
    if (this.pageId === this.cartPageId) {
      return this._items.filter((item) => item.product.stock.stockLevelStatus === 'outOfStock' && item.updateable);
    } else {
      return this._items.filter(
        (item) => item.backordered === true && item.product.stock.stockLevelStatus === 'outOfStock'
      );
    }
  }
  get freeGifts(): Item[] {
    if (this.pageId === this.cartPageId) {
      return this._items.filter((item) => !item.updateable);
    }
  }
  get discontinuedItems(): Item[] {
    return this._items.filter((item) => item.product.stock.stockLevelStatus === 'discontinued');
  }

  @Input() potentialProductPromotions: PromotionResult[] = [];
  @Input() promotionLocation: PromotionLocation = PromotionLocation.ActiveCart;

  @Input('cartIsLoading') set setLoading(value: boolean) {
    if (!this.readonly) {
      // Whenver the cart is loading, we disable the complete form
      // to avoid any user interaction with the cart.
      // value ? this.form.disable({ emitEvent: false }) : this.form.enable({ emitEvent: false });
    }
  }

  constructor(
    protected cartService: ActiveCartFacade,
    protected cartExtendedService: CartExtendService,
    private componentService: ComponentService,
    private cdr: ChangeDetectorRef,
    private messageService: GlobalMessageService,
    private featureConfig?: FeatureConfigService
  ) {}

  // TODO remove feature flag for #5958
  isSaveForLaterEnabled(): boolean {
    if (this.featureConfig) {
      return this.featureConfig.isEnabled('saveForLater');
    }
    return false;
  }
  // TODO remove feature flag for #5958

  /**
   * The items we're getting form the input do not have a consistent model.
   * In case of a `consignmentEntry`, we need to normalize the data from the orderEntry.
   */
  private resolveItems(items: Item[]): void {
    if (items.every((item) => item.hasOwnProperty('orderEntry'))) {
      this._items = items.map((consignmentEntry) => {
        const entry = Object.assign({}, (consignmentEntry as ConsignmentEntry).orderEntry);
        entry.quantity = consignmentEntry.quantity;
        return entry;
      });
    } else {
      this._items = items;
    }
  }

  private createForm(): void {
    this.form = new FormGroup({});
    this._items.forEach((item) => {
      const { code } = item.product;
      const { entryNumber } = item;
      const group = new FormGroup({
        entryNumber: new FormControl((item as any).entryNumber),
        quantity: new FormControl(item.quantity, { updateOn: 'submit' }),
        memo: new FormControl(item.memo, { updateOn: 'submit' }),
      });
      if (!item.updateable || this.readonly) {
        group.disable();
      }
      this.form.addControl(entryNumber + '_' + code, group);
    });
  }

  removeEntry(item: Item): void {
    const product = item.entryNumber + '_' + item.product.code;
    this.cartService.removeEntry(item);
    delete this.form.controls[product];
  }

  getControl(item: Item): Observable<FormGroup> {
    const product = item.entryNumber + '_' + item.product.code;
    const inStockFlag = item.product.inStockFlag;
    const stockLevelStats = item.product.stock.stockLevelStatus;
    return this.form.controls[product].valueChanges.pipe(
      // tslint:disable-next-line:deprecation
      startWith(<string>null),
      map((value) => {
        if (value) {
          this.cartService.updateEntry(value.entryNumber, value.quantity);
          if (value.memo !== null) {
            this.updateMemo(value, value.entryNumber, value.memo);
          }
          if (!inStockFlag && stockLevelStats === 'outOfStock') {
            this.showGlobalMessage('cart.cartValidationError.noStockError', GlobalMessageType.MSG_TYPE_ERROR);
          }
        }
      }),
      map(() => this.form.controls[product] as FormGroup)
    );
  }

  updateMemo(item: UdpateMemoPayload, entryNumber: number, memo: any) {
    this.cartExtendedService.udpateMemo(item, entryNumber, memo).subscribe((result) => {
      return result;
    });
  }

  getPotentialProductPromotionsForItem(item: Item): PromotionResult[] {
    const entryPromotions: PromotionResult[] = [];
    // don't show promotions in saveforlater
    if (this.options.isSaveForLater) {
      return entryPromotions;
    }
    if (this.potentialProductPromotions && this.potentialProductPromotions.length > 0) {
      for (const promotion of this.potentialProductPromotions) {
        if (promotion.description && promotion.consumedEntries && promotion.consumedEntries.length > 0) {
          for (const consumedEntry of promotion.consumedEntries) {
            if (this.isConsumedByEntry(consumedEntry, item)) {
              entryPromotions.push(promotion);
            }
          }
        }
      }
    }
    return entryPromotions;
  }

  getCartError(item: Item, status: string) {
    if (status === 'noStock' && typeof this.noStockError !== 'undefined') {
      // no stock
      const entries = this.noStockError;
      if (entries.indexOf(item.product.code.toString()) < 0) {
        return false;
      }
      return true;
    } else if (status === 'lowStock' && typeof this.lowStockError !== 'undefined') {
      // low stock
      const entries = this.lowStockError;
      if (entries.indexOf(item.product.code.toString()) < 0) {
        return false;
      }
      return true;
    }
  }

  private isConsumedByEntry(consumedEntry: any, entry: any): boolean {
    const consumedEntryNumber = consumedEntry.orderEntryNumber;
    if (entry.entries && entry.entries.length > 0) {
      for (const subEntry of entry.entries) {
        if (subEntry.entryNumber === consumedEntryNumber) {
          return true;
        }
      }
      return false;
    } else {
      return consumedEntryNumber === entry.entryNumber;
    }
  }

  private showGlobalMessage(text: string, messageType: GlobalMessageType) {
    this.messageService.add({ key: text }, messageType);
    const element: HTMLElement = document.getElementsByClassName('BottomHeaderSlot')[0] as HTMLElement;
    element.scrollIntoView();
  }
}
